import React, { useState, useEffect } from 'react';
import './Header.scss';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';
import SearchInput from '../SearchInput/SearchInput';
import UserInfo from '../UserInfo/UserInfo';
import { transitionTo } from '../../lib/pageTransition';

// Same sections as the mobile app's tabs. About, Terms, Privacy, Support and Log out
// live on the Profile page, which the profile photo opens (like the app's header button).
const NAV_LINKS = [
  { path: '/playlists', label: 'Playlists' },
  { path: '/artists', label: 'Artists' },
  { path: '/selections', label: 'Weekly Selections' },
];

const Header = () => {
  const { isAuthed, isFullscreen, setIsFullscreen, setIsMobileMenuOpen } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);
  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (location.pathname === path) {
      closeSidebar();
      return;
    }

    e.preventDefault();
    closeSidebar();
    // From the room: slide in. Page to page: just fade. To the room: slide out.
    transitionTo(navigate, path);
  };

  useEffect(() => {
    closeSidebar();
  }, [location]);

  useEffect(() => {
    setIsMobileMenuOpen(isOpen);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, setIsMobileMenuOpen]);

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <header className={`header ${isFullscreen ? 'header--fullscreen' : ''}`}>
      {/* Logo */}
      <div className="header__logo">
        <Link
          className="header__link"
          to="/"
          onClick={(e) => {
            e.preventDefault();
            setIsFullscreen(false);
            handleNavigate(e, '/');
          }}
        >
          <img className="header__logo__image" src="/svg/logo-color.svg" alt="Matt Digging" />
        </Link>
      </div>

      {/* SearchInput Component */}
      <SearchInput />

      {/* Desktop Navigation: Playlists · Artists · Weekly Selections · profile photo */}
      <nav className={`header__links header__links--desktop ${isFullscreen ? 'header__links--fullscreen' : ''}`}>
        {NAV_LINKS.map(({ path, label }) => (
          <div key={path} className={`header__links__item ${isActive(path) ? 'selected' : ''}`}>
            <Link to={path} onClick={(e) => handleNavigate(e, path)}>{label}</Link>
          </div>
        ))}

        {isAuthed ? (
          <Link
            to="/profile"
            className={`header__links__profile ${isActive('/profile') ? 'selected' : ''}`}
            onClick={(e) => handleNavigate(e, '/profile')}
            aria-label="Profile"
            title="Profile"
          >
            <UserInfo showName={false} />
          </Link>
        ) : (
          <button
            className="header__links__login"
            onClick={() => navigate('/login')}
          >
            Log in
          </button>
        )}
      </nav>

      {/* Mobile Burger Button */}
      <button
        className="burger-menu"
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </button>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="header__overlay"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar: profile (photo + name) at the top, then the same three sections */}
      <nav className={`header__mobile-menu ${isOpen ? 'header__mobile-menu--open' : ''}`}>
        {isAuthed ? (
          <Link
            className="header__mobile-menu__user-card"
            to="/profile"
            onClick={(e) => handleNavigate(e, '/profile')}
          >
            <UserInfo />
            <span className="header__mobile-menu__user-card__hint">Profile</span>
          </Link>
        ) : (
          <button className="header__mobile-menu__login" onClick={() => navigate('/login')}>Log in</button>
        )}

        <div className="header__mobile-menu__content">
          <span className="header__mobile-menu__section-label">Explore</span>

          {NAV_LINKS.map(({ path, label }, index) => (
            <Link
              key={path}
              className={`header__mobile-menu__item ${isActive(path) ? 'selected' : ''}`}
              to={path}
              onClick={(e) => handleNavigate(e, path)}
            >
              <span className="header__mobile-menu__item__index">{String(index + 1).padStart(2, '0')}</span>
              {label}
            </Link>
          ))}
        </div>
      </nav>
    </header>
  );
};

export default Header;